import { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { PackageCheck, Plus, Check, X, Eye, Image, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useArticleUnits } from '../hooks/useArticleUnits';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { downloadPdf } from '../utils/downloadPdf';
import { fmtDate } from '../lib/datetime';
import { emptyComprobante, comprobanteTotals } from '../components/PurchaseInvoiceForm';
import { ReceptionDetailDialog } from '../components/receptions/ReceptionDetailDialog';
import { ReviewReceptionDialog, AddFiscalInvoiceDialog, DeleteFiscalInvoiceDialog } from '../components/receptions/ReceptionActionDialogs';
import { NewReceptionDialog } from '../components/receptions/NewReceptionDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper para verificar permisos
const userCan = (user, module, action) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.permissions) {
    return user.permissions[module]?.[action] === true;
  }
  // Supervisor por defecto
  if (user.role === 'supervisor') {
    return ['ver', 'crear', 'aprobar', 'rechazar'].includes(action);
  }
  return false;
};

function Receptions() {
  const { user } = useOutletContext();
  const { unitOf } = useArticleUnits();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Permisos del usuario
  const canCreate = userCan(user, 'recepciones', 'crear');
  const canEdit = userCan(user, 'recepciones', 'editar');
  const canApprove = userCan(user, 'recepciones', 'aprobar');
  const canReject = userCan(user, 'recepciones', 'rechazar');
  // Permiso desacoplado: carga de comprobantes fiscales (datos impositivos).
  // El sector operativo NO lo tiene; Administración (ADMIN o habilitados) sí.
  const canLoadComprobantes = userCan(user, 'recepciones', 'comprobantes');
  
  // Filtro especial desde Dashboard
  const statusFromUrl = searchParams.get('status') || '';
  
  const [receptions, setReceptions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [overageDialogOpen, setOverageDialogOpen] = useState(false);
  const [overageNames, setOverageNames] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedReception, setSelectedReception] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Tolerancia de desvío global (clasificación automática Completa/Parcial/Condicionada)
  const [tolerancePct, setTolerancePct] = useState(0);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reception-params`, { withCredentials: true })
      .then(r => setTolerancePct(r.data.reception_tolerance_pct || 0))
      .catch(() => {});
  }, []);
  
  const [formData, setFormData] = useState({
    order_id: '',
    items: [],
    incident_reported: false,
    deviation_notes: '',
    invoice_date: '',
    invoice_number: '',
    invoice_type: '',
    evaluation: {
      product_conformity: 5,
      delivery_compliance: 5,
      order_integrity: 5,
      observations: ''
    }
  });
  
  const [articlesCatalog, setArticlesCatalog] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expenseConcepts, setExpenseConcepts] = useState([]);
  const [withholdingTypes, setWithholdingTypes] = useState([]);
  const [comprobante, setComprobante] = useState(emptyComprobante());
  // Comprobantes ADICIONALES (cuando el proveedor manda más de una FC en la misma recepción).
  // El primero queda en `comprobante`; los extras se agregan a este array.
  const [extraComprobantes, setExtraComprobantes] = useState([]);
  // Agregar FC a una recepción existente
  const [addCompTo, setAddCompTo] = useState(null);
  const [addComp, setAddComp] = useState(emptyComprobante());
  const [addingComp, setAddingComp] = useState(false);
  // Eliminar FC ligada a una recepción
  const canDeleteFC = user?.role === 'admin' || user?.role === 'supervisor' || user?.permissions?.compras_otras?.eliminar;
  const [deleteFC, setDeleteFC] = useState(null);
  const [deleteFCReason, setDeleteFCReason] = useState('');
  const [deletingFC, setDeletingFC] = useState(false);
  const handleDeleteFC = async () => {
    if (!deleteFC) return;
    setDeletingFC(true);
    try {
      const r = await axios.delete(`${BACKEND_URL}/api/purchase-invoices/${deleteFC.fc.purchase_invoice_id}`,
        { withCredentials: true, data: { reason: deleteFCReason.trim() } });
      const revs = r.data?.reversal_entries || [];
      toast.success(revs.length
        ? `Comprobante eliminado · Contra-asiento #${revs.map(x => x.entry_number).join(', #')} generado`
        : 'Comprobante eliminado');
      setDeleteFC(null); setDeleteFCReason('');
      const updated = await axios.get(`${BACKEND_URL}/api/receptions`, { withCredentials: true });
      const fresh = updated.data.find(r => r.reception_id === deleteFC.recId);
      if (fresh) setSelectedReception(fresh);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar el comprobante');
    } finally { setDeletingFC(false); }
  };
  const handleAddCompSubmit = async () => {
    if (!addCompTo) return;
    const t = comprobanteTotals(addComp);
    if (!addComp.tipo_comprobante || !addComp.punto_venta || !addComp.numero || !addComp.fecha_comprobante) {
      toast.error('Tipo, punto de venta, número y fecha son obligatorios'); return;
    }
    if (t.total <= 0) { toast.error('Cargá el detalle (neto/IVA)'); return; }
    setAddingComp(true);
    try {
      await axios.post(`${BACKEND_URL}/api/purchase-invoices/reception/${addCompTo.reception_id}`, {
        supplier_id: addComp.supplier_id || null,
        supplier_business_name: addComp.supplier_business_name || null,
        supplier_tax_id: addComp.supplier_tax_id || null,
        supplier_tax_condition: addComp.supplier_tax_condition || null,
        fecha_comprobante: addComp.fecha_comprobante,
        tipo_comprobante: addComp.tipo_comprobante,
        punto_venta: addComp.punto_venta || null,
        numero: addComp.numero || null,
        items_iva: (addComp.items_iva || []).filter(it => parseFloat(it.base_neta) > 0).map(it => ({ base_neta: parseFloat(it.base_neta), alicuota: parseFloat(it.alicuota) || 0 })),
        no_gravado: parseFloat(addComp.no_gravado) || 0,
        exento: parseFloat(addComp.exento) || 0,
        percepciones: (addComp.percepciones || []).filter(p => parseFloat(p.monto) > 0).map(p => ({ tipo: p.tipo, withholding_type_id: p.withholding_type_id || null, base: parseFloat(p.base) || 0, alicuota: p.alicuota ? parseFloat(p.alicuota) : null, monto: parseFloat(p.monto) })),
        attachment_id: addComp.attachment_id || null,
      }, { withCredentials: true });
      toast.success('Comprobante fiscal agregado y registrado en Libro Diario');
      // Control cruzado neto vs cantidades × precios (suma FCs previas + esta)
      const prevNeto = (addCompTo.fiscal_invoices || []).reduce((s, fc) => s + (fc.neto_gravado_total || 0) + (fc.no_gravado || 0) + (fc.exento || 0), 0);
      warnPriceMismatch(addCompTo.items, prevNeto + t.neto + t.exento);
      setAddCompTo(null);
      setAddComp(emptyComprobante());
      // refrescar y reabrir el detalle para mostrar la nueva FC
      const updated = await axios.get(`${BACKEND_URL}/api/receptions`, { withCredentials: true });
      const fresh = updated.data.find(r => r.reception_id === addCompTo.reception_id);
      if (fresh) setSelectedReception(fresh);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo agregar el comprobante');
    } finally { setAddingComp(false); }
  };
  const [reviewNotes, setReviewNotes] = useState('');

  // Control cruzado: avisar (sin bloquear) si el neto de los comprobantes difiere de Σ(cant × precio)
  const warnPriceMismatch = (items, compsNeto) => {
    const valued = (items || []).filter(it => (it.quantity_received || 0) > 0);
    if (!valued.length || valued.some(it => !(parseFloat(it.cost_price) > 0))) return;
    const itemsTotal = valued.reduce((s, it) => s + (parseInt(it.quantity_received) || 0) * (parseFloat(it.cost_price) || 0), 0);
    if (Math.abs(itemsTotal - compsNeto) > 0.01) {
      const f = (n) => '$ ' + Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      toast.warning(`Control cruzado: el neto de los comprobantes (${f(compsNeto)}) difiere de cantidades × precios de la recepción (${f(itemsTotal)}). Verificá descuentos, fletes o facturas parciales.`, { duration: 12000 });
    }
  };

  // Valorización de una recepción con precios pendientes (usuario con permiso de comprobantes)
  const [savingPrices, setSavingPrices] = useState(false);
  const handleSavePrices = async (reception, priceDraft) => {
    setSavingPrices(true);
    try {
      const { data: fresh } = await axios.put(
        `${BACKEND_URL}/api/receptions/${reception.reception_id}/prices`,
        { prices: priceDraft.map((p, i) => ((reception.items?.[i]?.quantity_received || 0) > 0 ? parseFloat(p) : 0)) },
        { withCredentials: true }
      );
      toast.success('Recepción valorizada: precios cargados en lotes, artículos e historial');
      setSelectedReception(fresh);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar la valorización');
    } finally { setSavingPrices(false); }
  };
  
  // Filtros de búsqueda
  // Filtros de búsqueda - inicializar con valor de URL si existe
  const [filters, setFilters] = useState({
    reception_number: '',
    order_number: '',
    received_by_name: '',
    status: statusFromUrl,
    has_fc: ''
  });

  // ¿La recepción tiene comprobante fiscal cargado? (para el filtro de Administración)
  const receptionHasFC = (r) => !!(
    r.has_fiscal_invoice ||
    (r.fiscal_invoices && r.fiscal_invoices.length > 0) ||
    r.fiscal_attachment_id ||
    r.invoice_image ||
    r.invoice_number
  );
  
  // Función para limpiar filtro especial
  const clearStatusFilter = () => {
    setFilters(prev => ({ ...prev, status: '' }));
    setSearchParams({});
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      // Cargar recepciones (obligatorio)
      const receptionsRes = await axios.get(`${BACKEND_URL}/api/receptions`, { withCredentials: true });
      setReceptions(receptionsRes.data);
      
      // Cargar OC pendientes solo si tiene permiso de crear recepciones
      // (endpoint dedicado gated por recepciones.crear — el receptor puede no tener ordenes_compra.ver)
      if (canCreate) {
        try {
          const ordersRes = await axios.get(`${BACKEND_URL}/api/purchase-orders/pending-reception`, { withCredentials: true });
          setPendingOrders(ordersRes.data || []);
        } catch (err) {
          setPendingOrders([]);
          toast.warning('No se pudieron cargar las OC pendientes de recepción');
        }
        // Cargar catálogo de artículos para detectar medicamentos
        try {
          const artRes = await axios.get(`${BACKEND_URL}/api/articles`, { withCredentials: true });
          const map = {};
          (artRes.data || []).forEach(a => { map[a.article_id] = a; });
          setArticlesCatalog(map);
        } catch (err) {
          setArticlesCatalog({});
        }
        try {
          const supRes = await axios.get(`${BACKEND_URL}/api/suppliers/active`, { withCredentials: true });
          setSuppliers(supRes.data || []);
        } catch (err) {
          setSuppliers([]);
        }
        try {
          const accRes = await axios.get(`${BACKEND_URL}/api/accounting/accounts?only_postable=true`, { withCredentials: true });
          setAccounts(accRes.data || []);
        } catch (err) {
          setAccounts([]);
        }
        try {
          const ecRes = await axios.get(`${BACKEND_URL}/api/treasury/expense-concepts`, { withCredentials: true });
          setExpenseConcepts(ecRes.data || []);
        } catch (err) {
          setExpenseConcepts([]);
        }
        try {
          const wRes = await axios.get(`${BACKEND_URL}/api/treasury/withholding-types/active`, { withCredentials: true });
          setWithholdingTypes(wRes.data || []);
        } catch (err) {
          setWithholdingTypes([]);
        }
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  const isMedicamento = (item) => {
    if (!item?.article_id) return false;
    return !!articlesCatalog[item.article_id]?.is_medicamento;
  };
  
  const handleOrderSelect = (orderId) => {
    const order = pendingOrders.find(o => o.order_id === orderId);
    setSelectedOrder(order);
    
    // Preparar items con campos para recepción (un renglón por item de la OC)
    const items = order?.items?.map(item => ({
      article_id: item.article_id,
      article_name: item.article_name,
      is_new_article: item.is_new_article || false,
      is_medicamento: item.is_medicamento ?? null,
      unit: item.unit || null,
      quantity_expected: item.quantity,
      quantity_received: item.quantity,
      cost_price: '',
      expiration_date: '',
      lot_number: ''
    })) || [];
    
    setFormData({ ...formData, order_id: orderId, items });

    // Pre-seleccionar el proveedor de la OC en el comprobante fiscal
    if (order?.supplier_id) {
      const sup = suppliers.find(s => s.supplier_id === order.supplier_id);
      setComprobante(c => ({
        ...c,
        supplier_id: order.supplier_id,
        supplier_business_name: sup?.business_name || order.supplier || '',
        supplier_tax_id: sup?.tax_id || '',
        supplier_tax_condition: sup?.tax_condition || '',
      }));
    }
  };
  
  // Calcula la cantidad pendiente para un article_id dado (suma esperada - suma recibida en otros renglones)
  const remainingForArticle = (articleId, excludeIndex = -1) => {
    const expectedItem = (selectedOrder?.items || []).find(i => i.article_id === articleId);
    const expected = expectedItem?.quantity || 0;
    const receivedSoFar = formData.items.reduce((acc, it, idx) => {
      if (idx === excludeIndex) return acc;
      if (it.article_id !== articleId) return acc;
      return acc + (parseInt(it.quantity_received) || 0);
    }, 0);
    return Math.max(0, expected - receivedSoFar);
  };
  
  const totalReceivedForArticle = (articleId) => {
    return formData.items.reduce((acc, it) => {
      if (it.article_id !== articleId) return acc;
      return acc + (parseInt(it.quantity_received) || 0);
    }, 0);
  };
  
  const expectedForArticle = (articleId) => {
    const exp = (selectedOrder?.items || []).find(i => i.article_id === articleId);
    return exp?.quantity || 0;
  };
  
  const handleAddLot = (index) => {
    const baseItem = formData.items[index];
    if (!baseItem?.article_id) {
      toast.error('No se puede agregar lote a un artículo sin ID');
      return;
    }
    const remaining = remainingForArticle(baseItem.article_id);
    const newLine = {
      article_id: baseItem.article_id,
      article_name: baseItem.article_name,
      is_new_article: baseItem.is_new_article || false,
      quantity_expected: 0, // los renglones adicionales no llevan "esperado" duplicado
      quantity_received: remaining,
      cost_price: baseItem.cost_price || '',
      expiration_date: '',
      lot_number: ''
    };
    const newItems = [...formData.items];
    newItems.splice(index + 1, 0, newLine);
    setFormData({ ...formData, items: newItems });
  };
  
  const handleRemoveLine = (index) => {
    const it = formData.items[index];
    // No permitir borrar el renglón original (el que tiene quantity_expected > 0)
    if (it.quantity_expected > 0) {
      toast.error('No podés eliminar el renglón principal del artículo');
      return;
    }
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar precios de costo — SOLO si el usuario valoriza (permiso de comprobantes).
    // Renglones con cantidad 0 (artículo que no ingresó) no requieren precio.
    if (canLoadComprobantes) {
      const hasZeroPrices = formData.items.some(item =>
        (parseInt(item.quantity_received) || 0) > 0 && !(parseFloat(item.cost_price) > 0));
      if (hasZeroPrices) {
        toast.error('Ingresa el precio neto unitario de todos los artículos recibidos');
        return;
      }
    }
    
    // Validar Nro. de Lote en medicamentos (solo si ingresó cantidad)
    for (const it of formData.items) {
      if ((parseInt(it.quantity_received) || 0) > 0 && isMedicamento(it) && !(it.lot_number || '').trim()) {
        toast.error(`"${it.article_name}" es medicamento: el Nro. de Lote es obligatorio`);
        return;
      }
    }
    
    // Validar evaluación obligatoria si la OC tiene proveedor vinculado
    const supplierId = selectedOrder?.supplier_id;
    if (supplierId) {
      const ev = formData.evaluation;
      const inRange = (v) => Number.isInteger(v) && v >= 1 && v <= 5;
      if (!inRange(ev.product_conformity) || !inRange(ev.delivery_compliance) || !inRange(ev.order_integrity)) {
        toast.error('Completá la evaluación del proveedor (1 a 5 en cada criterio)');
        return;
      }
    }
    
    // Incidencia reportada: la descripción es obligatoria
    if (formData.incident_reported && !(formData.deviation_notes || '').trim()) {
      toast.error('Describí la incidencia detectada (motivo obligatorio)');
      return;
    }

    // Validar exceso (advertencia) — el backend lo controla pero avisamos antes
    const tol = (tolerancePct || 0) / 100;
    const articleIds = [...new Set(formData.items.map(i => i.article_id).filter(Boolean))];
    const exceedingArticles = articleIds.filter(aid => {
      const exp = expectedForArticle(aid);
      const recv = totalReceivedForArticle(aid);
      return recv > exp && (recv - exp) > exp * tol;
    });
    if (exceedingArticles.length > 0) {
      const names = exceedingArticles.map(aid => formData.items.find(i => i.article_id === aid)?.article_name).join(', ');
      setOverageNames(names);
      setOverageDialogOpen(true);
      return; // El usuario debe confirmar primero
    }

    await proceedSubmit();
  };

  const proceedSubmit = async () => {
    setOverageDialogOpen(false);
    const supplierId = selectedOrder?.supplier_id;
    try {
      // Recolectar todos los comprobantes válidos (principal + extras).
      // Si el usuario NO tiene permiso de comprobantes (operativo), se omiten por completo.
      const allComps = canLoadComprobantes ? [comprobante, ...extraComprobantes] : [];
      const validComps = allComps.filter(c => {
        const t = comprobanteTotals(c);
        return t.total > 0 || !!c.attachment_id || !!c.numero;
      });
      const hasComprobante = validComps.length > 0;

      // Validar cada comprobante informado
      for (const c of validComps) {
        const t = comprobanteTotals(c);
        if (!c.tipo_comprobante || !c.punto_venta || !c.numero || !c.fecha_comprobante) {
          toast.error('Cada comprobante fiscal requiere tipo, punto de venta, número y fecha'); return;
        }
        if (t.total <= 0) { toast.error(`El comprobante ${c.tipo_comprobante} ${c.punto_venta}-${c.numero} no tiene importe (cargá neto/IVA)`); return; }
      }

      // El primero "manda" para los campos invoice_* del documento recepción (backcompat)
      const headComp = validComps[0] || comprobante;
      const payload = {
        ...formData,
        invoice_type: headComp.tipo_comprobante || formData.invoice_type,
        invoice_number: hasComprobante ? `${headComp.punto_venta || ''}-${headComp.numero || ''}`.replace(/^-|-$/g, '') : formData.invoice_number,
        invoice_date: headComp.fecha_comprobante || formData.invoice_date,
        items: formData.items.map(it => ({
          article_id: it.article_id,
          article_name: it.article_name,
          is_new_article: it.is_new_article,
          is_medicamento: it.is_medicamento ?? null,
          unit: it.unit || null,
          quantity_expected: it.quantity_expected,
          quantity_received: parseInt(it.quantity_received) || 0,
          cost_price: parseFloat(it.cost_price) || 0,
          expiration_date: it.expiration_date || null,
          lot_number: (it.lot_number || '').trim() || null
        }))
      };
      if (supplierId) {
        payload.evaluation = { ...formData.evaluation, supplier_id: supplierId };
      } else {
        delete payload.evaluation;
      }

      const response = await axios.post(`${BACKEND_URL}/api/receptions`, payload, { withCredentials: true });

      // Fase D: crear UN comprobante por cada uno informado (Libro IVA Compras + asiento por cada uno)
      let okCount = 0; let failCount = 0;
      for (const c of validComps) {
        try {
          await axios.post(`${BACKEND_URL}/api/purchase-invoices/reception/${response.data.reception_id}`, {
            supplier_id: c.supplier_id || null,
            supplier_business_name: c.supplier_business_name || null,
            supplier_tax_id: c.supplier_tax_id || null,
            supplier_tax_condition: c.supplier_tax_condition || null,
            fecha_comprobante: c.fecha_comprobante,
            tipo_comprobante: c.tipo_comprobante,
            punto_venta: c.punto_venta || null,
            numero: c.numero || null,
            items_iva: (c.items_iva || []).filter(it => parseFloat(it.base_neta) > 0).map(it => ({ base_neta: parseFloat(it.base_neta), alicuota: parseFloat(it.alicuota) || 0 })),
            no_gravado: parseFloat(c.no_gravado) || 0,
            exento: parseFloat(c.exento) || 0,
            percepciones: (c.percepciones || []).filter(p => parseFloat(p.monto) > 0).map(p => ({ tipo: p.tipo, withholding_type_id: p.withholding_type_id || null, base: parseFloat(p.base) || 0, alicuota: p.alicuota ? parseFloat(p.alicuota) : null, monto: parseFloat(p.monto) })),
            attachment_id: c.attachment_id || null,
          }, { withCredentials: true });
          okCount++;
        } catch (err) {
          failCount++;
          toast.warning(`Comprobante ${c.tipo_comprobante} ${c.punto_venta}-${c.numero}: ${err.response?.data?.detail || 'no pudo registrarse'}`);
        }
      }
      if (validComps.length > 1) {
        toast.success(`Recepción registrada con ${okCount} comprobante(s) fiscales`);
      } else {
        toast.success('Recepción registrada');
      }
      // Control cruzado neto vs cantidades × precios (solo si se informaron comprobantes)
      if (validComps.length > 0) {
        const compsNeto = validComps.reduce((s, c) => { const t = comprobanteTotals(c); return s + t.neto + t.exento; }, 0);
        warnPriceMismatch(payload.items, compsNeto);
      }
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar recepción');
    }
  };
  
  const resetForm = () => {
    setFormData({
      order_id: '',
      items: [],
      incident_reported: false,
      deviation_notes: '',
      invoice_date: '',
      invoice_number: '',
      invoice_type: '',
      evaluation: {
        product_conformity: 5,
        delivery_compliance: 5,
        order_integrity: 5,
        observations: ''
      }
    });
    setSelectedOrder(null);
    setComprobante(emptyComprobante());
    setExtraComprobantes([]);
  };
  
  const handleReview = async (action) => {
    if (!selectedReception) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/receptions/${selectedReception.reception_id}/review?action=${action}&notes=${encodeURIComponent(reviewNotes)}`,
        {},
        { withCredentials: true }
      );
      toast.success(action === 'approve' ? 'Recepción aprobada' : 'Recepción rechazada');
      setReviewDialogOpen(false);
      setSelectedReception(null);
      setReviewNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar revisión');
    }
  };
  
  const getStatusBadge = (reception) => {
    if (reception.final_status === 'approved') {
      return <Badge className="bg-green-100 text-green-800">Aprobada</Badge>;
    }
    if (reception.final_status === 'rejected') {
      return <Badge className="bg-red-100 text-red-800">Rechazada</Badge>;
    }
    
    const styles = {
      complete: { label: 'Completa', className: 'bg-green-100 text-green-800' },
      partial: { label: 'Parcial', className: 'bg-orange-100 text-orange-800' },
      conditional: { label: 'Condicionada', className: 'bg-amber-100 text-amber-800' }
    };
    const style = styles[reception.status] || styles.complete;
    return <Badge className={style.className}>{style.label}</Badge>;
  };
  
  const handleDownloadPDF = (receptionId, receptionNumber) => {
    downloadPdf(`/receptions/${receptionId}/pdf`, receptionNumber);
  };
  
  // Filtrar recepciones según los filtros
  const filteredReceptions = receptions.filter(reception => {
    const statusMatch = filters.status === '' || 
      (filters.status === 'complete' && reception.status === 'complete' && !reception.final_status) ||
      (filters.status === 'partial' && reception.status === 'partial' && !reception.final_status) ||
      (filters.status === 'conditional' && reception.status === 'conditional' && !reception.final_status) ||
      (filters.status === 'approved' && reception.final_status === 'approved') ||
      (filters.status === 'rejected' && reception.final_status === 'rejected');
    
    return (
      (filters.reception_number === '' || reception.reception_number?.toLowerCase().includes(filters.reception_number.toLowerCase())) &&
      (filters.order_number === '' || reception.order_number?.toLowerCase().includes(filters.order_number.toLowerCase())) &&
      (filters.received_by_name === '' || reception.received_by_name?.toLowerCase().includes(filters.received_by_name.toLowerCase())) &&
      statusMatch &&
      (filters.has_fc === '' ||
        (filters.has_fc === 'con' && receptionHasFC(reception)) ||
        (filters.has_fc === 'sin' && !receptionHasFC(reception)) ||
        (filters.has_fc === 'precios' && reception.prices_pending))
    );
  });
  
  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  
  return (
    <div className="space-y-6" data-testid="receptions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Recepciones</h1>
          <p className="text-muted-foreground">Registra la recepción de mercadería</p>
          
          {/* Indicador de filtro activo */}
          {statusFromUrl && (
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-amber-500 text-white flex items-center gap-1">
                <PackageCheck className="w-3 h-3" />
                Mostrando solo: {statusFromUrl === 'conditional' ? 'Condicionales' : statusFromUrl === 'complete' ? 'Completas' : statusFromUrl === 'partial' ? 'Parciales' : statusFromUrl}
                <button onClick={clearStatusFilter} className="ml-1 hover:bg-amber-600 rounded-full p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            </div>
          )}
        </div>
        
        {canCreate && pendingOrders.length > 0 && (
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-reception-button">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Recepción
          </Button>
        )}
      </div>
      
      {/* Alerta de OC pendientes */}
      {canCreate && pendingOrders.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <p className="font-medium text-blue-800">
              {pendingOrders.length} Orden(es) de Compra pendiente(s) de recepción
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Alerta de recepciones sin valorizar (para Administración) */}
      {canLoadComprobantes && receptions.some(r => r.prices_pending) && (
        <Card
          className="bg-orange-50 border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors"
          onClick={() => setFilters(prev => ({ ...prev, has_fc: prev.has_fc === 'precios' ? '' : 'precios' }))}
          data-testid="prices-pending-alert-card"
        >
          <CardContent className="py-4 flex items-center justify-between">
            <p className="font-medium text-orange-800">
              $ {receptions.filter(r => r.prices_pending).length} recepción(es) con precios pendientes de valorizar
            </p>
            <span className="text-xs text-orange-700 underline">
              {filters.has_fc === 'precios' ? 'Quitar filtro' : 'Ver solo pendientes'}
            </span>
          </CardContent>
        </Card>
      )}
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Input
                    placeholder="Filtrar número..."
                    value={filters.reception_number}
                    onChange={(e) => setFilters({ ...filters, reception_number: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>
                  <Input
                    placeholder="Filtrar OC..."
                    value={filters.order_number}
                    onChange={(e) => setFilters({ ...filters, order_number: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>
                  <Input
                    placeholder="Filtrar receptor..."
                    value={filters.received_by_name}
                    onChange={(e) => setFilters({ ...filters, received_by_name: e.target.value })}
                    className="h-8 text-xs"
                  />
                </TableHead>
                <TableHead>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="h-8 w-full text-xs rounded-md border border-input bg-background px-2"
                  >
                    <option value="">Todos</option>
                    <option value="complete">Completa</option>
                    <option value="partial">Parcial</option>
                    <option value="conditional">Condicionada</option>
                    <option value="approved">Aprobada</option>
                    <option value="rejected">Rechazada</option>
                  </select>
                </TableHead>
                <TableHead>
                  <select
                    value={filters.has_fc}
                    onChange={(e) => setFilters({ ...filters, has_fc: e.target.value })}
                    className="h-8 w-full text-xs rounded-md border border-input bg-background px-2"
                    data-testid="filter-fiscal-invoice"
                    title="Filtrar por comprobante fiscal"
                  >
                    <option value="">Factura: Todas</option>
                    <option value="con">Con comprobante</option>
                    <option value="sin">Sin comprobante fiscal</option>
                    <option value="precios">$ Precios pendientes</option>
                  </select>
                </TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReceptions.map((reception) => (
                <TableRow key={reception.reception_id}>
                  <TableCell className="font-mono font-medium">{reception.reception_number}</TableCell>
                  <TableCell>{fmtDate(reception.received_at)}</TableCell>
                  <TableCell className="font-mono text-sm">{reception.order_number}</TableCell>
                  <TableCell>{reception.received_by_name}</TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      {getStatusBadge(reception)}
                      {reception.prices_pending && (
                        <Badge className="bg-orange-100 text-orange-800" data-testid={`prices-pending-badge-${reception.reception_id}`}>
                          $ Precios pendientes
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {reception.fiscal_attachment_id ? (
                      <a
                        href={`${BACKEND_URL}/api/purchase-invoices/image/${reception.fiscal_attachment_id}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Ver comprobante adjunto"
                        data-testid={`reception-attachment-link-${reception.reception_id}`}
                      >
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer transition-colors">
                          <Image className="w-3 h-3 mr-1" />Ver adjunto
                        </Badge>
                      </a>
                    ) : (reception.invoice_image || reception.has_fiscal_invoice) ? (
                      <Badge className="bg-green-100 text-green-800"><Image className="w-3 h-3 mr-1" />Adjunta</Badge>
                    ) : (
                      <Badge variant="outline">Sin factura</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setSelectedReception(reception); setDetailDialogOpen(true); }}
                        title="Ver detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDownloadPDF(reception.reception_id, reception.reception_number)}
                        title="Descargar PDF"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      
                      {reception.status === 'conditional' && !reception.final_status && canApprove && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => { setSelectedReception(reception); setReviewDialogOpen(true); }}
                          title="Revisar"
                          className="text-amber-600"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredReceptions.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              <PackageCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay recepciones que coincidan con los filtros.</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Dialog para crear Recepción */}
      <NewReceptionDialog
        open={createDialogOpen}
        onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}
        onCancel={() => setCreateDialogOpen(false)}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        pendingOrders={pendingOrders}
        selectedOrder={selectedOrder}
        handleOrderSelect={handleOrderSelect}
        isMedicamento={isMedicamento}
        unitOf={unitOf}
        totalReceivedForArticle={totalReceivedForArticle}
        expectedForArticle={expectedForArticle}
        handleAddLot={handleAddLot}
        handleRemoveLine={handleRemoveLine}
        comprobante={comprobante}
        setComprobante={setComprobante}
        extraComprobantes={extraComprobantes}
        setExtraComprobantes={setExtraComprobantes}
        suppliers={suppliers}
        accounts={accounts}
        expenseConcepts={expenseConcepts}
        withholdingTypes={withholdingTypes}
        canLoadComprobantes={canLoadComprobantes}
        tolerancePct={tolerancePct}
      />

      
      {/* Dialog de detalle */}
      <ReceptionDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        reception={selectedReception}
        user={user}
        unitOf={unitOf}
        canDeleteFC={canDeleteFC}
        canLoadComprobantes={canLoadComprobantes}
        onAddComp={setAddCompTo}
        onDeleteFC={setDeleteFC}
        onSavePrices={handleSavePrices}
        savingPrices={savingPrices}
      />
      
      <ReviewReceptionDialog
        open={reviewDialogOpen}
        onOpenChange={setReviewDialogOpen}
        reception={selectedReception}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        onReview={handleReview}
        onCancel={() => setReviewDialogOpen(false)}
      />

      <AddFiscalInvoiceDialog
        target={addCompTo}
        onClose={() => { setAddCompTo(null); setAddComp(emptyComprobante()); }}
        value={addComp}
        setValue={setAddComp}
        suppliers={suppliers}
        accounts={accounts}
        expenseConcepts={expenseConcepts}
        withholdingTypes={withholdingTypes}
        adding={addingComp}
        onSubmit={handleAddCompSubmit}
      />



      <ConfirmDialog
        open={overageDialogOpen}
        onOpenChange={setOverageDialogOpen}
        title="Exceso de cantidad detectado"
        description={<>Hay exceso de cantidad en: <strong>{overageNames}</strong>.<br/><br/>La recepción se marcará como <strong>CONDICIONADA</strong> y requerirá aprobación del supervisor. ¿Continuar?</>}
        confirmText="Continuar"
        destructive={false}
        onConfirm={proceedSubmit}
        testId="confirm-overage"
      />

      <DeleteFiscalInvoiceDialog
        deleteFC={deleteFC}
        onClose={() => { setDeleteFC(null); setDeleteFCReason(''); }}
        deleting={deletingFC}
        onConfirm={handleDeleteFC}
        reason={deleteFCReason}
        setReason={setDeleteFCReason}
      />


    </div>
  );
}

export default Receptions;
